import { type GridColDef } from '@mui/x-data-grid-pro'
import { RosterSourceStatus, SortIndexField } from '__generated__/graphql'
import { useMemo } from 'react'
import ProcessStatusPill from 'client/candidate-table-temp/components/ProcessStatusPill'
import { SourceStatusPill } from 'client/candidate-table-temp/components/SourceStatusPill'
import { GetCandidatesForClientPortalSql } from 'client/candidate-table-temp/graphql/candidateSearch.gql'
import ProcessStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditCell'
import SourceStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/SourceStatusEditCell'
import { generateRowId } from 'client/candidate-table-temp/components/RosterCandidatesTable/utils'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

interface UseOnRosterSpecificColumnDefsParams {
    canEdit: boolean
    rosterId: string
}

export default function useOnRosterSpecificColumnDefs({
    canEdit,
    rosterId,
}: UseOnRosterSpecificColumnDefsParams): Record<
    string,
    RosterCandidateTableColumnDefinition
> {
    const onRosterSpecificColumnDefs = useMemo(() => {
        return {
            processStatus: {
                field: 'processStatus',
                headerName: 'Process Status',
                editable: canEdit ?? false,
                sortable: true,
                sortIndexField: SortIndexField.ProcessStatus,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <ProcessStatusEditCell
                        rosterId={rosterId}
                        canEdit={canEdit ?? false}
                        api={api}
                        currentProcessStatus={
                            row.rosterCandidate?.candidateProcessStatus
                                ?.processStatus
                        }
                        refetchQueries={[GetCandidatesForClientPortalSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: (params) => {
                    return (
                        <ProcessStatusPill
                            processStatusId={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus?.id
                            }
                        />
                    )
                },
            },
            sourceStatus: {
                field: 'sourceStatus',
                headerName: 'Source Status',
                editable: canEdit ?? false,
                sortable: false,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <SourceStatusEditCell
                        api={api}
                        disabled={!row.rosterCandidate || !(canEdit ?? false)}
                        currentSourceStatus={
                            row.rosterCandidate?.sourceStatus ??
                            RosterSourceStatus.NoStatus
                        }
                        refetchQueries={[GetCandidatesForClientPortalSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: ({ row }) => (
                    <SourceStatusPill
                        status={row.rosterCandidate?.sourceStatus}
                    />
                ),
            },
        }
    }, [canEdit, rosterId])

    return onRosterSpecificColumnDefs
}
